import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import { Box } from '../../components/UI-CHAKRA/Box';
import { Text } from '../../components/UI-CHAKRA/Text';
import { CERTIFIER_STATUS } from './certificates';
import { ReactComponent as AlertIcon } from '../../assets/images/icons/mevo/alert.svg';
import { ReactComponent as CloseIcon } from '../../assets/images/icons/mevo/close-mono-color.svg';
import { Flex } from '../../components/UI-CHAKRA/Flex';

const cardStyles = {
  recentError: {
    border: '2px solid #868E96',
  },
  active: {
    border: '2px solid #3484FA',
    boxShadow: '0px 0px 8px rgba(113, 138, 240, 0.3)',
  },
  notActive: {
    border: '',
    boxShadow: '',
  },
};

export const LogoImg = styled.img`
  max-height: 40px;
  max-width: 100px;
  object-fit: contain;
  text-align: center;
`;

interface CertificateCardProps {
  active: boolean;
  clickable: boolean;
  imagePath: string | null;
  name: string;
  certifierStatus: CERTIFIER_STATUS;
}

export default function CertificateCard(props: CertificateCardProps) {
  const [HoverComponent, setHoverComponent] = useState<React.ReactNode | null>(null);

  const shouldShowCertifierStatus =
    props.certifierStatus === CERTIFIER_STATUS.INACTIVE || props.certifierStatus === CERTIFIER_STATUS.PARTIALLY_ACTIVE;

  const certifiedHadRecentError = props.certifierStatus === CERTIFIER_STATUS.RECENT_ERROR;

  const CertifierLabel = useMemo(() => {
    if (!shouldShowCertifierStatus) {
      return (
        <Text
          align={'center'}
          fontSize={'14px'}
          lineHeight={'normal'}
          dangerouslySetInnerHTML={{ __html: props.name }}
          color="#868E96"
          fontWeight={600}
        />
      );
    }

    return (
      <Flex alignItems={'center'} justifyContent={'center'} fill={'red'}>
        <Text
          align={'center'}
          fontSize={'12px'}
          lineHeight={'16px'}
          dangerouslySetInnerHTML={{ __html: props.name }}
          mr={'5px'}
        />
        {props.certifierStatus === CERTIFIER_STATUS.INACTIVE ? (
          <CloseIcon fill="#FF6161" width={'13px'} height={'13px'} data-testid={'certifier-inactive'} />
        ) : (
          <AlertIcon width={'13px'} height={'13px'} data-testid={'certifier-instable'} />
        )}
      </Flex>
    );
  }, [props.name, shouldShowCertifierStatus, props.certifierStatus]);

  const onMouseEnter = () => {
    if (!shouldShowCertifierStatus) return null;

    return setHoverComponent(
      <Flex
        pos={'absolute'}
        top={0}
        bottom={0}
        left={0}
        right={0}
        bgColor={
          props.certifierStatus === CERTIFIER_STATUS.INACTIVE ? 'rgba(255, 97, 97, 0.9)' : 'rgba(252, 178, 36, 0.9)'
        }
        flexDir={'column'}
        justifyContent={'center'}
        alignItems={'center'}
        p={'10px'}
        borderRadius={'18px'}
      >
        <Box mb={'5px'}>
          {props.certifierStatus === CERTIFIER_STATUS.INACTIVE ? (
            <CloseIcon fill="white" width={'20px'} height={'20px'} />
          ) : (
            <AlertIcon width={'20px'} height={'20px'} />
          )}
        </Box>
        <Text
          fontSize={'12px'}
          fontWeight={600}
          lineHeight={'13px'}
          textAlign={'center'}
          color={props.certifierStatus === CERTIFIER_STATUS.INACTIVE ? 'white' : 'typography.dark'}
        >
          Certificadora
          {props.certifierStatus === CERTIFIER_STATUS.INACTIVE ? ' indisponível' : ' instável'}
        </Text>
      </Flex>
    );
  };

  return (
    <Flex
      opacity={certifiedHadRecentError && !props.active ? 0.6 : 1}
      flexDirection={'column'}
      justifyContent={'space-between'}
      gap={'12px'}
      height="100%"
      borderRadius={'20px'}
      bgColor={'white'}
      border={
        certifiedHadRecentError && !props.active
          ? cardStyles.recentError.border
          : props.active
            ? cardStyles.active.border
            : cardStyles.notActive.border
      }
      boxShadow={props.active ? cardStyles.active.boxShadow : cardStyles.notActive.boxShadow}
      minHeight={'120px'}
      cursor={props.clickable ? 'pointer' : 'normal'}
      onMouseEnter={onMouseEnter}
      onMouseLeave={() => setHoverComponent(null)}
      position={'relative'}
    >
      {HoverComponent}

      <Flex flexDirection="column" gap="10px" minHeight="120px" height="100%" p="10px">
        <Flex flex={2} flexDir="column" justifyContent="center" gap="10px" alignItems="center">
          {props.imagePath ? (
            <LogoImg src={props.imagePath} style={{ margin: '0 auto' }} alt="" />
          ) : (
            <Text textAlign={'center'} fontSize={'12px'} dangerouslySetInnerHTML={{ __html: props.name }} />
          )}
          {CertifierLabel}
        </Flex>
        {(props.active || certifiedHadRecentError) && (
          <Flex
            bgColor={certifiedHadRecentError ? '#868E96' : '#718AF0'}
            p="5px"
            borderRadius="10px"
            justifyContent="center"
            textAlign={'center'}
          >
            <Text fontSize="10px" fontWeight={700} lineHeight="none" color="white">
              {certifiedHadRecentError ? 'Certificado Instável' : 'Certificado Ativo'}
            </Text>
          </Flex>
        )}
      </Flex>
    </Flex>
  );
}
